import { Card, Form, Input, Select, Button, message } from "antd";
import { useRef, useState, useEffect } from "react";

const VIDEO_MAX_MB = 50;

export default function FoodDetails({
  form,
  setCoverImage,
  initialImage,
  isCoverRequired = false,
  mediaVideoFile,
  setMediaVideoFile,
  initialMediaUrl,
  videoRemoved,
  setVideoRemoved
}) {
  const fileRef = useRef(null);
  const objectUrlRef = useRef(null);
  const videoFileRef = useRef(null);
  const videoObjectUrlRef = useRef(null);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);

  useEffect(() => {
    if (initialImage) {
      setPreviewUrl(initialImage);
    }
  }, [initialImage]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      if (videoObjectUrlRef.current) {
        URL.revokeObjectURL(videoObjectUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (videoObjectUrlRef.current) {
      URL.revokeObjectURL(videoObjectUrlRef.current);
      videoObjectUrlRef.current = null;
    }

    if (mediaVideoFile) {
      const url = URL.createObjectURL(mediaVideoFile);
      videoObjectUrlRef.current = url;
      setVideoPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        videoObjectUrlRef.current = null;
      };
    }

    if (initialMediaUrl && !videoRemoved) {
      setVideoPreviewUrl(initialMediaUrl);
    } else {
      setVideoPreviewUrl(null);
    }
  }, [mediaVideoFile, initialMediaUrl, videoRemoved]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    // clear
    if (!file) {
      setCoverImage(null);
      form.setFieldValue("coverImage", null);
      form.validateFields(["coverImage"]).catch(() => { });
      setPreviewUrl(initialImage || null);
      e.target.value = null;
      return;
    }

    // type
    if (!file.type.startsWith("image/")) {
      message.error("Only image files allowed");
      setCoverImage(null);
      form.setFieldValue("coverImage", null);
      form.validateFields(["coverImage"]).catch(() => { });
      e.target.value = null;
      return;
    }

    // size
    const isTooLarge = file.size / 1024 / 1024 > 2;
    if (isTooLarge) {
      message.error("Image must be smaller than 2MB");
      setCoverImage(null);
      form.setFieldValue("coverImage", null);
      form.validateFields(["coverImage"]).catch(() => { });
      e.target.value = null;
      return;
    }

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }

    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;

    setCoverImage(file);
    form.setFieldValue("coverImage", file);
    form.validateFields(["coverImage"]).catch(() => { });
    setPreviewUrl(url);

    e.target.value = null;
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      e.target.value = null;
      return;
    }

    if (!file.type.startsWith("video/")) {
      message.error("Only video files are allowed");
      e.target.value = null;
      return;
    }

    const isTooLarge = file.size / 1024 / 1024 > VIDEO_MAX_MB;
    if (isTooLarge) {
      message.error(`Video must be smaller than ${VIDEO_MAX_MB}MB`);
      e.target.value = null;
      return;
    }

    setMediaVideoFile(file);
    setVideoRemoved(false);
    e.target.value = null;
  };

  const handleRemoveVideo = () => {
    setMediaVideoFile(null);
    setVideoRemoved(true);
    if (videoFileRef.current) {
      videoFileRef.current.value = null;
    }
  };

  return (
    <Card className="recipe-section">
      <div className="section-title">
        <span className="section-bar" />
        <h3>1. Add Food Details</h3>
      </div>

      <div className="media-uploads-grid">
        <Form.Item shouldUpdate noStyle>
          {() => {
            const coverError = form.getFieldError("coverImage")?.[0];
            const isError = !!coverError;

            return (
              <Form.Item
                label={
                  <>
                    Cover Image{" "}
                    {isCoverRequired && (
                      <span style={{ color: "#ff4d4f", marginLeft: 2 }}>*</span>
                    )}
                  </>
                }
                validateStatus={isError ? "error" : ""}
                help={coverError || ""}
                className="media-upload-col"
              >
                <div className="cover-block">
                  <div
                    className="cover-upload-box"
                    onClick={() => fileRef.current?.click()}
                    style={{ cursor: "pointer" }}
                  >
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 8
                        }}
                      />
                    ) : (
                      "+ Add Photo"
                    )}
                  </div>

                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />

                  <div className="cover-actions">
                    <Button
                      className="upload-btn"
                      onClick={() => fileRef.current?.click()}
                    >
                      Upload image
                    </Button>

                    <Button disabled className="preview-btn">
                      Preview Page
                    </Button>
                  </div>
                </div>

                <Form.Item
                  name="coverImage"
                  noStyle
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!isCoverRequired) return Promise.resolve();
                        if (value || initialImage) return Promise.resolve();
                        return Promise.reject(
                          new Error("Cover image is required")
                        );
                      }
                    }
                  ]}
                >
                  <Input type="hidden" />
                </Form.Item>
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item label="Video" className="media-upload-col video-field">
          <div className="cover-block video-block">
            <div
              className="cover-upload-box video-upload-box"
              onClick={(e) => {
                if (e.target.closest?.("video")) return;
                videoFileRef.current?.click();
              }}
              style={{ cursor: "pointer" }}
            >
              {videoPreviewUrl ? (
                <video
                  key={videoPreviewUrl}
                  src={videoPreviewUrl}
                  controls
                  playsInline
                  className="video-preview-el"
                />
              ) : (
                <span className="video-placeholder">
                  <span className="video-placeholder-icon" aria-hidden>
                    ▶
                  </span>
                  Add video (optional)
                </span>
              )}
            </div>

            <input
              ref={videoFileRef}
              type="file"
              accept="video/*"
              style={{ display: "none" }}
              onChange={handleVideoChange}
            />

            <div className="cover-actions video-actions">
              <Button
                className="upload-btn"
                onClick={() => videoFileRef.current?.click()}
              >
                {videoPreviewUrl ? "Replace video" : "Upload video"}
              </Button>
              {videoPreviewUrl ? (
                <Button danger type="default" onClick={handleRemoveVideo}>
                  Remove video
                </Button>
              ) : null}
            </div>
          </div>
        </Form.Item>
      </div>

      <Form.Item
        label="Recipe Title"
        name="title"
        rules={[
          { required: true, message: "Title is required" },
          { whitespace: true, message: "Title cannot be empty" }
        ]}
      >
        <Input placeholder="Enter title" />
      </Form.Item>

      <Form.Item label="Recipe Title (Arabic)" name="titleAr">
        <Input placeholder="أدخل العنوان بالعربية" dir="rtl" />
      </Form.Item>

      <Form.Item label="Recipe Description" name="description">
        <Input.TextArea
          autoSize={{ minRows: 4 }}
          placeholder="Enter a short description"
          style={{
            borderRadius: 10,
            padding: "14px 16px",
            fontSize: 14,
            resize: "vertical"
          }}
        />
      </Form.Item>

      <Form.Item label="Recipe Description (Arabic)" name="descriptionAr">
        <Input.TextArea
          autoSize={{ minRows: 4 }}
          placeholder="أدخل وصفاً قصيراً بالعربية"
          dir="rtl"
          style={{
            borderRadius: 10,
            padding: "14px 16px",
            fontSize: 14,
            resize: "vertical"
          }}
        />
      </Form.Item>

      <Form.Item
        label="Diet Type"
        name="dietType"
        rules={[{ required: true, message: "Select diet type" }]}
      >
        <Select placeholder="Select Diet Type">
          <Select.Option value="lose_weight">
            Weight loss / Calorie deficit
          </Select.Option>
          <Select.Option value="low_carb">
            Low carb / Keto
          </Select.Option>
          <Select.Option value="high_protein">
            High protein / Muscle gain
          </Select.Option>
          <Select.Option value="vegetarian">
            Vegetarian
          </Select.Option>
          <Select.Option value="vegan">
            Vegan
          </Select.Option>
          <Select.Option value="pescatarian">
            Pescatarian
          </Select.Option>
        </Select>
      </Form.Item>

      <div className="two-cols">
        <Form.Item
          label="Cuisine Type"
          name="cuisineType"
          rules={[{ required: true, message: "Select cuisine type" }]}
        >
          <Select placeholder="Select Cuisine Type">
            <Select.Option value="chinese">Chinese</Select.Option>
            <Select.Option value="indian">Indian</Select.Option>
            <Select.Option value="middle_eastern">Middle Eastern</Select.Option>
            <Select.Option value="italian">Italian</Select.Option>
            <Select.Option value="american">American</Select.Option>
            <Select.Option value="continental">Continental</Select.Option>
            <Select.Option value="french">French</Select.Option>
            <Select.Option value="japanese">Japanese</Select.Option>
            <Select.Option value="korean">Korean</Select.Option>
            <Select.Option value="greek">Greek</Select.Option>
            <Select.Option value="mexican">Mexican</Select.Option>
            <Select.Option value="turkish">Turkish</Select.Option>
            <Select.Option value="mediterranean">Mediterranean</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Meal Type"
          name="mealType"
          rules={[{ required: true, message: "Select meal type" }]}
        >
          <Select mode="multiple" placeholder="Select Meal Type">
            <Select.Option value="snack">Snacks</Select.Option>
            <Select.Option value="dessert">Dessert</Select.Option>
            <Select.Option value="breakfast">Breakfast</Select.Option>
            <Select.Option value="lunch">Lunch</Select.Option>
            <Select.Option value="dinner">Dinner</Select.Option>
          </Select>
        </Form.Item>
      </div>

      <div className="two-cols">
        <Form.Item label="Total Time*">
          <div className="time-row">
            <Form.Item
              name="totalTimeValue"
              noStyle
              rules={[{ required: true, message: "Enter time" }]}
            >
              <Input
                type="number"
                min={0}
                placeholder="30"
                style={{ width: "70%" }}
              />
            </Form.Item>

            <Form.Item name="totalTimeUnit" noStyle initialValue="Minutes">
              <Select style={{ width: "30%" }}>
                <Select.Option value="Minutes">Minutes</Select.Option>
                <Select.Option value="Hours">Hours</Select.Option>
              </Select>
            </Form.Item>
          </div>
        </Form.Item>

        <Form.Item
          label="Servings"
          name="servings"
          rules={[{ required: true, message: "Enter servings" }]}
        >
          <Input type="number" min={1} placeholder="1" />
        </Form.Item>
      </div>
    </Card>
  );
}